'use client';

import React, { FC } from 'react';

import classNames from 'classnames';

import {
  DynamicComponent,
  ExternalLink,
  LuvImage,
  UniversalLink,
} from '@/components';

import Heading, {
  HeadingSize,
  HeadingTag,
} from '@/components/ui/Heading/Heading';

import {
  COMPONENT_MAPPING_FOOTER_SECTIONS,
  TW_FOOTER_GRID_AREAS,
} from '@/core/constants';

import {
  TypeFooterSectionFields,
  TypeLink_external,
  TypeLink_internal,
} from '@/core/generated-types';

import { Link } from '@/navigation';

import { shouldHideWithLocaleOverride } from '@/utils/localeOverride';

import Overridable from '../Overridable/Overridable';

interface FooterSectionProps {
  fields: TypeFooterSectionFields;
}

const footerColumnClass = 'mb-6';

const h4HeaderClasses =
  'mb-2 whitespace-normal text-base capitalize tracking-normal font-bold';

const ulStrippedClasses = 'm-0 list-none p-0 flex-wrap gap-y-2';

const liClasses = 'pb-2';

const AppSection: FC<FooterSectionProps> = ({ fields }) => {
  const { title, footerSection, footerLinks } = fields;

  const appLink = footerLinks?.[0];

  if (appLink?.sys.contentType.sys.id === 'link_external') {
    const {
      fields: { linkText, linkImage, linkUrl = '/' },
    } = appLink as TypeLink_external;

    return (
      <div
        className={classNames(
          footerColumnClass,
          TW_FOOTER_GRID_AREAS[
            footerSection as keyof typeof TW_FOOTER_GRID_AREAS
          ],
          'grid grid-cols-2 grid-rows-1',
        )}
        data-testid="app-section"
      >
        <div>
          <Heading
            tag={HeadingTag.H4}
            size={HeadingSize.H4}
            classes={h4HeaderClasses}
            text={title}
            hoverEffect
          />

          <Link className="text-sm text-indigo" href={linkUrl}>
            {linkText}
          </Link>
        </div>

        <div>
          {linkImage && (
            <figure>
              <LuvImage imageData={linkImage} width={129} height={44} alt="" />
            </figure>
          )}
        </div>
      </div>
    );
  }

  return null;
};

const SocialSection: FC<FooterSectionProps> = ({ fields }) => {
  const { title, footerSection } = fields;

  const footerLinks = fields.footerLinks as TypeLink_external[];

  return (
    <div
      className={classNames(
        footerColumnClass,
        TW_FOOTER_GRID_AREAS[
          footerSection as keyof typeof TW_FOOTER_GRID_AREAS
        ],
      )}
      data-testid="social-section"
    >
      <h4 className={h4HeaderClasses}>{title}</h4>

      <ul className={`flex max-w-[200px] flex-row ${ulStrippedClasses} mb-4`}>
        {footerLinks?.map((link) => {
          const { sys, fields } = link;

          const { linkUrl, linkImage } = fields;

          if (!linkUrl) {
            return null;
          }

          return (
            <li key={sys.id} className="my-auto flex-auto">
              <ExternalLink link={link} classes="h-7 w-7">
                {linkImage && (
                  <figure className="inline-block">
                    <LuvImage imageData={linkImage} />
                  </figure>
                )}
              </ExternalLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const SelectorsSection: FC<FooterSectionProps> = ({ fields }) => {
  const { footerSection } = fields;

  return (
    <div
      className={
        TW_FOOTER_GRID_AREAS[footerSection as keyof typeof TW_FOOTER_GRID_AREAS]
      }
      data-testid="selectors-section"
    >
      <ul className={classNames(ulStrippedClasses, 'min-w-[204px] pb-2')}>
        {fields?.footerLinks?.map((dropDown) => {
          const { sys } = dropDown;

          if (shouldHideWithLocaleOverride(dropDown.fields.localeOverride)) {
            return null;
          }

          return (
            <li key={sys.id} className="mb-6" data-testid="language-selector">
              <DynamicComponent component={dropDown} />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const CredentialsSection: FC<FooterSectionProps> = ({ fields }) => {
  const { footerSection } = fields;

  const footerLinks = fields.footerLinks as TypeLink_external[];

  return (
    <div
      className={classNames(
        footerColumnClass,
        TW_FOOTER_GRID_AREAS[
          footerSection as keyof typeof TW_FOOTER_GRID_AREAS
        ],
      )}
      data-testid="credentials-section"
    >
      {footerLinks?.map((link) => {
        const { sys, fields } = link;

        const { linkUrl, linkImage } = fields;

        if (!linkUrl) {
          return null;
        }

        return (
          <ExternalLink key={sys.id} link={link} classes="w-12">
            {linkImage && (
              <figure className="inline-block">
                <LuvImage imageData={linkImage} />
              </figure>
            )}
          </ExternalLink>
        );
      })}
    </div>
  );
};

const Links: FC<FooterSectionProps> = ({ fields }) => {
  if (!fields) {
    return null;
  }

  const { title, footerLinks, footerSection } = fields;

  return (
    <div
      className={classNames(
        footerColumnClass,
        TW_FOOTER_GRID_AREAS[footerSection || 'general'],
      )}
      data-testid="links"
    >
      {title && (
        <Heading
          tag={HeadingTag.H4}
          size={HeadingSize.H4}
          classes={h4HeaderClasses}
          text={title}
          hoverEffect
        />
      )}

      <ul className={ulStrippedClasses}>
        {footerLinks?.map((footerLink) => (
          <Overridable key={footerLink.sys.id} content={footerLink}>
            <li key={footerLink.sys.id} className={liClasses}>
              <UniversalLink
                link={footerLink as TypeLink_internal | TypeLink_external}
                classes="text-indigo text-sm"
              />
            </li>
          </Overridable>
        ))}
      </ul>
    </div>
  );
};

const FooterSection: FC<FooterSectionProps> = (props) => {
  const footerSection = props.fields?.footerSection;

  switch (footerSection) {
    case COMPONENT_MAPPING_FOOTER_SECTIONS.app:
      return <AppSection {...props} />;
    case COMPONENT_MAPPING_FOOTER_SECTIONS.social:
      return <SocialSection {...props} />;
    case COMPONENT_MAPPING_FOOTER_SECTIONS.selectors:
      return <SelectorsSection {...props} />;
    case COMPONENT_MAPPING_FOOTER_SECTIONS.credentials:
      return <CredentialsSection {...props} />;
    default:
      return <Links {...props} />;
  }
};

export default React.memo(FooterSection);
